import React from 'react';
import { Row, Col } from 'antd';
import COSelect from 'shared/components/COSelect';
import InputField from 'shared/components/InputField';
import Map from 'shared/components/map';
import clsx from 'clsx';
import { HelpDeskInputFieldEnum } from 'context/HelpDeskTooltipContext/helpDeskInputFieldEnum';
import { ReactComponent as Arrow } from 'assets/icons/arrow.svg';
import styles from './editLocation.module.scss';
import { OrganizationMetaData } from 'models/OrganizationMetaData.ts';
import { Location } from 'models/ActivityDataEntryLocation/location.model';

interface LocationDetailsFormProps {
  isLocationEdit: boolean;
  setFieldValue: Function;
  values: Location;
  locationFullAddress: string | undefined;
  categoryList: { label: string; value: string; key: string }[];
  orgListMeta: OrganizationMetaData[] | undefined;
  isCompleted: boolean | undefined;
  handleToolTipOpen: (fieldName?: HelpDeskInputFieldEnum) => () => void;
  handleModalOpen: () => void;
  searchPlaceByCountryProvince: (
    country?: string,
    province?: string,
    setFieldValue?: Function
  ) => () => Promise<void>;
  setAddressInState: Function;
}

const LocationDetailsForm: React.FC<LocationDetailsFormProps> = ({
  isLocationEdit,
  setFieldValue,
  values,
  locationFullAddress,
  categoryList,
  orgListMeta,
  isCompleted,
  handleToolTipOpen,
  handleModalOpen,
  searchPlaceByCountryProvince,
  setAddressInState,
}) => {
  const { locationName, locationCategory, locationAddress } =
    HelpDeskInputFieldEnum;

  return (
    <div className={styles.locationsContainerArea}>
      <Row gutter={[12, 12]} className={styles.rowContainer}>
        <Col span={18}>
          <InputField
            name="locationName"
            placeholderstring="Enter The Name Of Facility Or Warehouse"
            label="Location Name"
            disabled={!isLocationEdit}
            info
            infoClick={handleToolTipOpen(locationName)}
          />
        </Col>
        <Col span={6}>
          <COSelect
            name="locationCategory"
            placeholderstring="Location Category"
            label="Location Category"
            options={categoryList}
            disabled={!isLocationEdit}
            info
            infoClick={handleToolTipOpen(locationCategory)}
          />
        </Col>
      </Row>
      {isLocationEdit ? (
        <Row gutter={[12, 12]} className={styles.rowContainer}>
          <Col span={24}>
            <Map
              name="locationsMap"
              setAddress={setAddressInState(setFieldValue)}
              latitude={values?.location?.latitude}
              longitude={values?.location?.longitude}
              info
              infoClick={handleToolTipOpen(locationAddress)}
            />
          </Col>
        </Row>
      ) : (
        <Row gutter={[12, 12]} className={styles.rowContainer}>
          <Col span={24}>
            <InputField
              name="location"
              placeholderstring="Location"
              label="Location"
              value={locationFullAddress}
              disabled={!isLocationEdit}
              info
              infoClick={handleToolTipOpen(locationAddress)}
            />
          </Col>
        </Row>
      )}
      <Row gutter={[32, 12]} className={styles.rowContainer}>
        <Col span={6}>
          <InputField
            name="location.street"
            placeholderstring="Street, Locality"
            label="Street"
            disabled={!isLocationEdit}
          />
        </Col>
        <Col span={6}>
          <InputField
            name="location.province"
            placeholderstring="Province"
            label="Province"
            disabled={!isLocationEdit}
            onBlur={searchPlaceByCountryProvince(
              values.location?.country ?? '',
              values.location?.province ?? '',
              setFieldValue
            )}
          />
        </Col>
        <Col span={6}>
          <InputField
            name="location.city"
            placeholderstring="City"
            label="City"
            disabled={!isLocationEdit}
          />
        </Col>
        <Col span={6}>
          <InputField
            name="location.postalCode"
            placeholderstring="Postal Code"
            label="Postal Code"
            disabled={!isLocationEdit}
          />
        </Col>
      </Row>
      <Row gutter={[32, 12]}>
        <Col span={6}>
          <COSelect
            name="organizationId"
            placeholderstring="Belongs To"
            label="Belongs To"
            options={orgListMeta}
            disabled={!isLocationEdit}
          />
        </Col>
        <Col span={6}>
          <div className={styles.orgBoundaryContainer}>
            <div className={styles.orgBoundaryText} onClick={handleModalOpen}>
              Organizational boundary
              <Arrow className={styles.arrowIcon} fontSize={44} />
            </div>
            <div
              className={clsx(
                isCompleted
                  ? styles.orgBoundaryStatus
                  : styles.orgBoundaryPendingStatus
              )}>
              {isCompleted ? 'Completed' : 'Not Completed'}
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default LocationDetailsForm;
